/*
 *  Cart Drawer
 *    Provides scripts for the cart drawer.
 *
 *  Version:
 *    1.0.0 - 2020/03/16
 */

import * as ResponsiveSizes from './../../settings/Responsive';
import { register, ShopifySection } from './../ShopifySection';
import { Accordion } from './../../objects/accordion/Accordion';
import { CartQuantitySelector } from './../../objects/cart/CartQuantitySelector';
import { CartCrossSellLineItems, CROSS_SELL_SELECTOR } from './../../objects/cart/CartCrossSellLineItems';
import { CartPromoMessage, SPEND_SAVE_TITLE_SELECTOR } from './../../objects/cart/CartPromoMessage';
import { InitialzeSVGImages } from './../../objects/icon/Icon';
import { CART_TEMPLATE_SUBTOTAL, CART_TEMPLATE_SUBTOTAL_COMPARE, CART_TEMPLATE_SUBTOTAL_DISCOUNT, CART_TEMPLATE_SUBTOTAL_SNS_MESSAGE,
  CART_TEMPLATE_AFTERPAY, CART_TEMPLATE_CHECKOUT, CART_TEMPLATE_COUNTER, CART_TEMPLATE_BODY, SELECTOR_ACCORDION,
  CART_TEMPLATE_HAS_ITEMS, CART_TEMPLATE_NO_ITEMS, CART_TEMPLATE_PRODUCT, CART_TEMPLATE_PRODUCTS, CART_TEMPLATE_REMOVE } from './../../templates/cart';
import { generatePricing } from './../../objects/pricing/ProductPricing';

import {
  getCurrentCart, getVariant, removeFromCart, ON_CART_FINISHED, ON_CART_PENDING, pictureGenerate, generateIcon, printMoney, escapeString, t
} from '@process-creative/slate-theme-tools';
import { drawGift } from '../gift/FreeGift';

//Selectors
export const CONTAINER_SELECTOR = '[data-cart-drawer]';
export const CART_DRAWER_TOGGLE = '[data-cart-drawer-toggle]';
export const CART_DRAWER_PRODUCTS = '[data-cart-drawer-products]';
export const CART_DRAWER_PRODUCT = '[data-cart-template-product]';
export const CART_PRODUCT_REMOVE = '[data-cart-template-remove]';
export const CART_UPSELL_CLOSE = '[data-cart-upsell-close]';
export const CART_UPSELL_ADD = '[data-cart-upsell-add-btn]';
export const CART_DRAWER_SUBTOTAL = '[data-cart-drawer-subtotal]';
export const CART_ITEM_JSON = '[data-item-json]';
export const CART_PRODUCTS = '[data-cart-template-products]';
export const CART_UPSELLS = '[data-cross-sell-container]';
export const CART_UPSELLS_BODY = '[data-cross-sell-body]';
export const CART_UPSELL_PRODUCT = '[data-cross-sell-template-product]';


//Classes
export const CLASS_CART_DRAWER_OPEN = 'is-cart-open';
export const CLASS_HAS_ITEMS = 'has-items';
export const CLASS_HIDDEN = 'is-hidden';
export const CLASS_PENDING = 'is-pending';
export const CLASS_DISABLED = 'is-disabled';
export const CLASS_ACTIVE = 'is-active';
export const CLASS_NO_UPSELLS = 'has-no-upsells';

export const ATTR_SAVINGS = 'data-savings';
export const ATTR_VARIANT_ID = 'data-variant_id'
export const ATTR_PRODUCT_ID = 'data-product-id';

const UPSELL_PRICE_MAX = 10000; // $100 max price for upsells
const UPSELL_LIMIT = 2; // Show 2 products


const STR_ADD = t('products.product.add_to_cart');
const STR_UPSELL_TITLE = t('products.product.upsell_title');
export class CartDrawer extends ShopifySection {
  constructor(container) {
    super(container);

    this.quantitySelectors = [];

    this.body = $('body');
    this.cartPending = false;

    this.refreshElements();

    //Events
    this.setupQuantitySelectors();
    $(document).find(CART_TEMPLATE_BODY).removeClass(CLASS_PENDING);

    if ($(document).find(`[${SPEND_SAVE_TITLE_SELECTOR}]`).length) new CartPromoMessage(container);
    if ($(document).find(CART_UPSELL_PRODUCT).length) {
      new CartCrossSellLineItems($(document).find(CROSS_SELL_SELECTOR));
    } else {
      this.addUpsellProducts();
    }
    
    // calling accordion function
    new Accordion($(SELECTOR_ACCORDION));

    //Events
    this.addDrawerListeners();

    $(document).on(ON_CART_PENDING, () => this.onCartPending());
    $(document).on(ON_CART_FINISHED, () => this.onCartFinished());
  }

  refreshElements(){
    let container = this.container;
    this.products = container.find(CART_TEMPLATE_PRODUCTS);
    this.drawerTargets = [this.body, this.container];
    this.cartCounter = container.find(CART_TEMPLATE_COUNTER);
    this.cartDrawerProducts = container.find(CART_DRAWER_PRODUCTS);
    this.cartUpsell = container.find(CROSS_SELL_SELECTOR);
    this.cartHasItems = container.find(CART_TEMPLATE_HAS_ITEMS);
    this.cartNoItems = container.find(CART_TEMPLATE_NO_ITEMS);
    this.cartSubTotal = container.find(CART_TEMPLATE_SUBTOTAL);
    this.cartAfterpayFinalPrice = container.find(CART_TEMPLATE_AFTERPAY);
    this.cartProducts = container.find(CART_DRAWER_PRODUCT);
    this.cartUpsells = container.find(CART_UPSELLS);
    this.cartProductsWrapper = container.find(CART_PRODUCTS);
    this.cartUpsellsBody = container.find(CART_UPSELLS_BODY);
  }

  async fetchUpsells () {
    let productID = this.cartProducts[0].getAttribute(ATTR_PRODUCT_ID);
    const fetchURL = `/recommendations/products.json?product_id=${productID}&limit=10`;
    try {
      const response = await fetch(fetchURL).then(response => response.json());
      return response.products;
    } catch (error) {
      console.warn('Failed to get recommended products!');
      console.warn(error);
    }
  }

  /**  
   * filters fetched products so they're under the price limit and not in cart
   * Limits to upsell limit
   */
  async filterFetchedUpsells(){
    let cart = getCurrentCart();
    let cartVariantIds = cart.items.map(item => item.variant_id);
    let filteredProducts = [];
    let upsellFetch = await this.fetchUpsells();

    upsellFetch.forEach(product => {
      let productVariantIds = product.variants.map(variant => variant.id);
      if (product.price <= UPSELL_PRICE_MAX && 
        !cartVariantIds.some(cartVariantId => productVariantIds.includes(cartVariantId)) && 
        filteredProducts.length < UPSELL_LIMIT){
        filteredProducts.push(product);
      }
    });

   return filteredProducts;
  }

  /** 
   * Creates HTML for upsell line items
   */
  async drawUpsells(){
    let products = await this.filterFetchedUpsells();
    if (!products.length) return false;
    let html = '';
    products.forEach((product, i) => {
      let { title, variants, url } = product;
      let productImage = product.featured_image;
      let variant = variants.find(variant => variant.available === true);
      let { price, id } = variant;
      let variantImage = variant.featured_image;
      let image = variantImage;

      if (!variantImage) image = productImage;

      html += `
      <div class="c-cart-line" data-cross-sell-template-product
        data-index="${i}" data-item="${id}"
      >
        <div class="c-cart-line__image">
          ${pictureGenerate({ src: image.src,
            srcSize: 80,
            sizes: [
              { size: 60, screen: 375 },
              { size: 80, screen: 750 }
            ],
            class:'c-cart-line__image-img'
          }).replace('<img', '<img loading="lazy"')}
        </div>
        <div class="c-cart-line__content">
          <div class="c-cart-line__content-desc">
            <a href="${url}" class="c-cart-line__content-desc-link">
              <span class="c-cart-line__content-desc-title">
                ${title}
              </span>
            </a>
            <div class="c-cart-line__content-price">
              <div class="c-cart-line__content-price-single">
                <span data-currency data-money="${price}">
                  ${printMoney(price)}
                </span>
              </div>
            </div>
          </div>
          <button
            type="submit"
            class="o-btn o-product-thumbnail__add-button"
            data-cross-sell-add-button
          >
            ${STR_ADD}
          </button>
        </div>
      </div>
      `
    });

    return html;
  }

  /**
   * Injects upsells if they are any to display
   */
  async addUpsellProducts(){
    let cart = getCurrentCart();
    if (cart.item_count === 0) return;
    let html = await this.drawUpsells();
    if (!html) {
      this.cartUpsells[0].innerHTML = '';
      this.cartUpsells[0].classList.add(CLASS_HIDDEN);
    } else {
      this.cartUpsells[0].innerHTML = `
      <div class="c-cross-sell" data-cross-sell>
        <div class="c-cross-sell__title-inner">
          <p class="c-cross-sell__title">${STR_UPSELL_TITLE}</p>
        </div>
        <div class="c-cross-sell__body-container" data-cross-sell-body>
          ${html}
        </div>
      </div>`;
      this.cartUpsells[0].classList.remove(CLASS_HIDDEN);
      new CartCrossSellLineItems($(document).find(CROSS_SELL_SELECTOR));      
    }
  }

  async fetchCart() {
    let data;
    try {
      data = await new Promise((resolve, reject) => {
        $.ajax({
          url: '/cart',
          data: { view: 'ajax' },
          success: d => resolve(d) ,
          error: (jqXHR,status,error) => reject(error)
        });
      });
    } catch(e) {
      //Error when fetching cart body
      console.error(e);
      drawGift();
      return;
    }

    let cart = getCurrentCart();
    let cartContent = '';

    //Remove existing cart drawer listeners
    this.removeDrawerListeners();

    let cartHasItems = $(CART_TEMPLATE_HAS_ITEMS);
    let cartNoItems = $(CART_TEMPLATE_NO_ITEMS);

    //Do we have items in the cart?
    if(cart.items.length) {
      cartContent = data;


      //Update Classes
      cartNoItems.addClass(CLASS_HIDDEN);
      cartHasItems.removeClass(CLASS_HIDDEN);
    } else {
      //Update Classes
      cartNoItems.removeClass(CLASS_HIDDEN);
      cartHasItems.addClass(CLASS_HIDDEN);
    }

    //Update cart contents
    $('.c-cart-drawer__inner').html(cartContent);
    if (!$('.c-cart-drawer .c-cart-template__form').hasClass('c-cart-drawer__wrapper')) {
      $('.c-cart-drawer .c-cart-template__form').addClass('c-cart-drawer__wrapper')
    }

    this.refreshElements();

    new Accordion($(SELECTOR_ACCORDION));

    InitialzeSVGImages();

    if ($(document).find(`[${SPEND_SAVE_TITLE_SELECTOR}]`).length) new CartPromoMessage($(document).find(`[${SPEND_SAVE_TITLE_SELECTOR}]`));
    if ($(document).find(CART_UPSELL_PRODUCT).length) {
      new CartCrossSellLineItems($(document).find(CROSS_SELL_SELECTOR));
    } else {
      this.addUpsellProducts();
    }
  
    let savings = cart.total_discount;
    let cartProductArr = Array.from(this.cartProducts);
    if (cartProductArr.length){
      cartProductArr.forEach(product => {
        if (product.hasAttribute(ATTR_SAVINGS)) {
          let itemSavings = parseInt(product.getAttribute(ATTR_SAVINGS));
          let itemVariantID = parseInt(product.getAttribute(ATTR_VARIANT_ID));
          let itemObject = cart.items.find(item => item.variant_id === itemVariantID);
          if (!itemObject) return;
          let itemQty = itemObject.quantity;
          savings += itemSavings * itemQty;
        }
      });
    }

    let cartSubTotalDiscount = $(document).find(CART_TEMPLATE_SUBTOTAL_DISCOUNT);
    let cartSubTotalCompare = $(document).find(CART_TEMPLATE_SUBTOTAL_COMPARE);
    let cartSubTotalSNSMessage = $(document).find(CART_TEMPLATE_SUBTOTAL_SNS_MESSAGE);
    
    //Update Prices
    if (cart.original_total_price > cart.items_subtotal_price) {
      cartSubTotalCompare.addClass(CLASS_ACTIVE);
      cartSubTotalCompare.find('.c-cart-body__footer-subtotal-price-discount').html(generatePricing(cart.original_total_price));
    } else if (cart.original_total_price > cart.total_price) {
      cartSubTotalCompare.addClass(CLASS_ACTIVE);
      cartSubTotalCompare.find('.c-cart-body__footer-subtotal-price-discount').html(generatePricing(cart.original_total_price))
    } else {
      cartSubTotalCompare.removeClass(CLASS_ACTIVE);
    }

    if (savings > 0) {
      cartSubTotalDiscount.addClass(CLASS_ACTIVE);
      cartSubTotalDiscount.find('.c-cart-body__footer-subtotal-price-discount-span').html(`${generatePricing(savings)}`);
      cartSubTotalSNSMessage.addClass(CLASS_ACTIVE);
    } else {
      cartSubTotalDiscount.removeClass(CLASS_ACTIVE);
      cartSubTotalSNSMessage.removeClass(CLASS_ACTIVE);
    }


    this.cartSubTotal.html(`
      ${generatePricing(
        cart.items_subtotal_price > cart.total_price ? 
        cart.total_price: 
        cart.items_subtotal_price,
        null,'')}
    `);

    this.cartAfterpayFinalPrice.attr('data-money', cart.items_subtotal_price);
    this.cartAfterpayFinalPrice.html(printMoney((cart.items_subtotal_price/4)));

    this.setupQuantitySelectors();
    this.updateCartCount();

    //Add listeners after elements redrawn
    this.addDrawerListeners();

    // $(document).trigger('onCartDrawn');
    drawGift();
  }

  onCartToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (location.pathname === '/cart') {
      this.close();
      return;
    };
    
    if(this.container.hasClass(CLASS_CART_DRAWER_OPEN)) {
      this.close();
    } else {
      this.open();
    }
  }


  open() {
    //Set the height of the screen to prevent background scrolling
    let width = $(window).width();
    let vpH = $(window).innerHeight();
    // setTimeout(() => { this.body.css('height', `${vpH.toString()}px`); }, 500);

    this.drawerTargets.forEach(e => $(e).addClass(CLASS_CART_DRAWER_OPEN));
    if(width > ResponsiveSizes.small-1) return;
  }

  close() {
    this.drawerTargets.forEach(e => $(e).removeClass(CLASS_CART_DRAWER_OPEN));
    this.body.css('height', '');
  }


  onCartPending(){
    //Update Classes
    this.cartPending = true;
    $(document).find(CART_TEMPLATE_BODY).addClass(CLASS_PENDING);
    $(document).find(CART_TEMPLATE_CHECKOUT).addClass(CLASS_DISABLED);
    $(document).find(CART_TEMPLATE_CHECKOUT).attr('disabled', 'disabled');
  }

  onCartFinished() {
    //Update Classes
    this.cartPending = false;

    const onCartPendFinished = () => {
      if(this.cartPending) return;
      $(document).find(CART_TEMPLATE_BODY).removeClass(CLASS_PENDING);
      $(document).find(CART_TEMPLATE_CHECKOUT).removeClass(CLASS_DISABLED);
      $(document).find(CART_TEMPLATE_CHECKOUT).removeAttr('disabled');
      drawGift();

      if (typeof Yotpo !== 'undefined') {
        var api = new Yotpo.API(yotpo);
        api.refreshWidgets();
      }
    }
    this.fetchCart()
      .then(e => onCartPendFinished())
      .catch(e => console.error(e))
    ;
  }


  removeItem = (e) => {
    e.preventDefault(), e.stopPropagation();

    let self = $(e.currentTarget);
    let product = self.closest(CART_TEMPLATE_PRODUCT);
    let index = product.attr('data-index');

    //Now remove Item
    try {
      removeFromCart(index);
      this.fetchCart();
    } catch(e) {
      console.error(e);
    }
  }

  addDrawerListeners() {
    this.cartToggleEls = document.querySelectorAll(CART_DRAWER_TOGGLE);
    this.cartToggleEls.forEach(el => el.addEventListener('click', this.onCartToggle));
    this.cartRemoveEls = document.querySelectorAll(CART_TEMPLATE_REMOVE);
    this.cartRemoveEls.forEach(el => el.addEventListener('click', this.removeItem));
  }

  removeDrawerListeners() {
    this.cartToggleEls.forEach(el => el.removeEventListener('click', this.onCartToggle));
    this.cartRemoveEls.forEach(el => el.removeEventListener('click', this.removeItem));
  }

  setupQuantitySelectors() {
    //Destroy
    this.quantitySelectors.forEach(e => e.destroy());

    //Now recreate
    this.quantitySelectors = [];

    let products = $(CART_TEMPLATE_PRODUCT);
    products.each((e, i) => {
      let self = $(i);
      let index = self.attr('data-index');
      this.quantitySelectors.push(new CartQuantitySelector(this, index, self));
    });
  }

  updateCartCount() {
    let cart = getCurrentCart();
    let count = $('[data-header-container] [data-cart-counter');
    if (!count) return;
    if(cart.item_count > 0) {
      count.text(cart.item_count)
    } else {
      count.text('');
    };
  }  
}

register(CONTAINER_SELECTOR, CartDrawer);